import { CalendarDays, Eye, Gauge, History, MessagesSquare, Target } from "lucide-react";

import { Summary } from "../../../../components/ui/Summary";
import { CrmPetDiagnosisController } from "../../../../services/controllers/crmPetDiagnosis";
import { useCrmPetDiagnosis } from "../../../../store/crmPetDiagnosis/useCrmPetDiagnosis";
import {
  formatDateTime,
  formatNumber,
  formatPercent,
  getUsageStatus,
} from "../helpers";
import { EmptyState } from "../components/EmptyState";
import { EventBreakdown } from "./EventBreakdown";
import { MetricCard } from "../components/MetricCard";
import { SectionCard } from "../components/SectionCard";
import { SectionError } from "../components/SectionError";

export function AdoptionSummary() {
  const controller = CrmPetDiagnosisController();
  const usage = useCrmPetDiagnosis((state) => state.data.usage);
  const loading = useCrmPetDiagnosis((state) => state.data.loading.usage);
  const error = useCrmPetDiagnosis((state) => state.data.errors.usage);
  const status = getUsageStatus(usage.summary?.usageStatus);
  const metrics = [
    {
      id: "crm-adoption-status",
      icon: Gauge,
      value: <span className="text-base font-semibold">Status de uso</span>,
      label: status.label,
      tooltip: "Classificação de utilização informada pela consolidação do CRM Pet.",
    },
    {
      id: "crm-adoption-contact-days",
      icon: MessagesSquare,
      value: <span className="text-base font-semibold">Dias com contatos</span>,
      label: formatNumber(usage.summary?.daysWithUse),
      tooltip: "Quantidade de dias do período em que houve ao menos um contato registrado.",
    },
    {
      id: "crm-adoption-view-days",
      icon: CalendarDays,
      value: <span className="text-base font-semibold">Dias com visualizações</span>,
      label: formatNumber(usage.summary?.daysWithView),
      tooltip: "Quantidade de dias do período em que houve visualização ou interação registrada.",
    },
    {
      id: "crm-adoption-contact-coverage",
      icon: Target,
      value: <span className="text-base font-semibold">Cobertura</span>,
      label: formatPercent(usage.summary?.contactCoverage),
      tooltip: "Cobertura de contatos calculada pela consolidação de utilização do CRM Pet.",
    },
    {
      id: "crm-adoption-last-use",
      icon: History,
      value: <span className="text-base font-semibold">Último uso</span>,
      label: formatDateTime(usage.summary?.lastUseAt),
      tooltip: "Data e hora da utilização mais recente encontrada no período selecionado.",
    },
    {
      id: "crm-adoption-interactions",
      icon: Eye,
      value: <span className="text-base font-semibold">Interações</span>,
      label: formatNumber(usage.summary?.views),
      tooltip: "Total de eventos classificados como visualização ou interação no período.",
    },
  ];

  return (
    <SectionCard
      title="Adoção da ferramenta"
      description="Frequência de utilização e interações registradas no CRM Pet."
      icon={Gauge}
    >
      {error && <SectionError message={error} onRetry={() => controller.retrySection("usage")} />}
      {!error && !loading && !usage.summary && (
        <EmptyState
          title="Consolidação ainda não gerada"
          description="A utilização do CRM Pet ainda não foi consolidada para o período."
        />
      )}
      {!error && (loading || usage.summary) && (
        <div className="gap-6 flex flex-wrap">
          <Summary.Root className="flex-1 min-w-[515px]">
            {metrics.map((metric) => <MetricCard key={metric.id} {...metric} loading={loading} className="!min-w-[250px]" />)}
          </Summary.Root>
          <div className="flex-1 min-w-[400px]">
            <h3 className="mb-4 text-sm font-semibold text-gray-800">Eventos no período</h3>
            {loading && <div className="h-48 animate-pulse rounded-md bg-gray-100" />}
            {!loading && <EventBreakdown daily={usage.daily} />} </div>
        </div>
      )}
    </SectionCard>
  );
}
